import { useMemo } from 'react'
import { DataTable } from '@klear/ui/components/data-table'
import type { ColumnDef } from '@tanstack/react-table'
import { Money } from '@klear/money'
import { useTrader } from '@klear/ui/hooks/use-trader'
import { usePayables } from '@klear/ui/hooks/use-payables'
import { useCurrencyScopedReport } from '@klear/ui/hooks/use-currency-scoped-report'
import { CurrencyReportSelector } from '@klear/ui/components/currency-report-selector'
import { DataTableColumnHeader } from '@klear/ui/components/data-table-column-header'
import { unicodeFlag } from '@klear/ui/lib/utils'
import {
  ORGANIZATION_SIZES,
  PERMISSION_ACTION_ADD,
  PERMISSION_ACTION_VIEW,
  PERMISSION_ENTITY_PAYMENT,
  PERMISSION_ENTITY_SUPPLIER,
  ROUTE_PAYABLE,
  ROUTE_SUPPLIERS
} from '@klear/constants'
import { COUNTRY_CODE_OPTIONS } from '@klear/constants/countries'
import RowActions from '@/components/row-actions'
import { useSuppliers, useSupplierMutations, type SupplierRow } from '@klear/ui/hooks/use-suppliers'
import { useParams, Link } from 'react-router'
import { ButtonLink } from '@klear/ui/components/button-link'
import { CircleDollarSign, PlusIcon } from 'lucide-react'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { GrowingSuppliersCard, DecliningSuppliersCard } from '@/components/supplier-growth-cards'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

const filters = [
  {
    title: 'Country',
    column: 'country',
    options: COUNTRY_CODE_OPTIONS
  }
]

const createColumns = (
  urlPrefixApp: string,
  deleteMutation: (params: { id: number }) => Promise<unknown>,
  isDeleting: boolean
) => {
  const columns: ColumnDef<SupplierRow>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Organization name" />,
      cell: ({ row }) => <div className="">{row.original.name}</div>,
      enableSorting: true,
      enableHiding: true
    },
    {
      accessorKey: 'size',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Organization size" />,
      cell: ({ row }) => {
        const label = ORGANIZATION_SIZES.find((size) => size === row.original.size)

        return (
          <div className="flex space-x-2">
            <span className="max-w-[500px] truncate font-medium">{label}</span>
          </div>
        )
      }
    },
    {
      id: 'country',
      accessorFn: (row) => row.country,
      header: ({ column }) => <DataTableColumnHeader column={column} title="Organization country" />,
      cell: ({ row }) => {
        const countryCode = row.original.country
        const countryFlag = unicodeFlag(countryCode)

        return (
          <div className="flex items-center gap-2">
            <span>{countryFlag}</span>
            <span>{countryCode}</span>
          </div>
        )
      },
      filterFn: (row, _id, value) => value.includes(row.original.country),
      enableSorting: true,
      enableHiding: true
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <RowActions
          row={row}
          appPath={`${urlPrefixApp}/${ROUTE_SUPPLIERS}`}
          resourceType={PERMISSION_ENTITY_SUPPLIER}
          getEditTo={(id) => `${urlPrefixApp}/${ROUTE_SUPPLIERS}/${id}/edit`}
          getResourceName={(supplier) => supplier.name}
          deleteMutation={deleteMutation}
          isDeleting={isDeleting}
        />
      )
    }
  ]

  return columns
}

export default function Suppliers() {
  useDocumentTitle('Suppliers')
  const { traderId } = useParams()
  const { urlPrefixApp } = useTrader(traderId)
  const { suppliers, isLoading } = useSuppliers(parseInt(traderId || '0'))
  const { deleteSupplier, isDeleting } = useSupplierMutations()
  const { user } = useAuth()

  const traderIdNum = parseInt(traderId || '0')
  const showGrowthCards = user.hasPermission(PERMISSION_ENTITY_PAYMENT, PERMISSION_ACTION_VIEW)

  const { payables } = usePayables(traderIdNum)
  const payableMonies = useMemo(
    () => payables.map((payable) => payable.amount).filter((money): money is Money => money != null),
    [payables]
  )
  const { selectedCurrency, setSelectedCurrency, showCurrencySelector, availableCurrencies } = useCurrencyScopedReport([
    payableMonies
  ])

  const deleteSupplierWrapper = (params: { id: number }) =>
    deleteSupplier({
      id: params.id,
      traderId: traderIdNum
    })

  return (
    <>
      <div className="h-full flex-1 flex-col space-y-8 p-8 md:flex">
        <div className="flex items-center justify-between space-y-2">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Suppliers</h2>
            <p className="text-muted-foreground">View and manage your suppliers.</p>
          </div>
          <div className="flex flex-col gap-2">
            {user.hasPermission(PERMISSION_ENTITY_SUPPLIER, PERMISSION_ACTION_ADD) && (
              <ButtonLink Link={Link} to={`${urlPrefixApp}/${ROUTE_SUPPLIERS}/new`}>
                <PlusIcon className="mr-2 h-4 w-4" />
                Add Supplier
              </ButtonLink>
            )}
            <ButtonLink Link={Link} variant="outline" className="w-full" to={`${urlPrefixApp}/${ROUTE_PAYABLE}`}>
              <CircleDollarSign className="mr-2 h-4 w-4" />
              Create Payable
            </ButtonLink>
          </div>
        </div>
        {showGrowthCards && (
          <div className="space-y-4">
            {showCurrencySelector && (
              <div className="flex justify-end">
                <CurrencyReportSelector
                  value={selectedCurrency}
                  onChange={setSelectedCurrency}
                  currencies={availableCurrencies}
                />
              </div>
            )}
            <div className="grid gap-4 md:grid-cols-2">
              <GrowingSuppliersCard selectedCurrency={selectedCurrency} traderId={traderIdNum} />
              <DecliningSuppliersCard selectedCurrency={selectedCurrency} traderId={traderIdNum} />
            </div>
          </div>
        )}
        <DataTable
          data={suppliers}
          columns={createColumns(urlPrefixApp, deleteSupplierWrapper, isDeleting)}
          filters={filters}
          loading={isLoading}
        />
      </div>
    </>
  )
}
